import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { bonusService } from 'components/api/bonuses';
import { useAppDispatch } from 'store/hooks';
import { showToast } from 'store/toastSlice';
import { formatDate } from 'utils/date';
import { commonGridStyles } from 'styles/gridStyles';
import CustomPagination from 'components/common/pagination/pagination';
import styles from '../userDetails.module.scss';

interface UserBonusesProps {
  userId: string;
}

interface UserBonusItem {
  id: number;
  bonusType: string;
  amount: number;
  status: string;
  createdAt: string;
}

const UserBonuses: React.FC<UserBonusesProps> = ({ userId }) => {
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState(0);

  // User bonuses list state
  const [bonuses, setBonuses] = useState<UserBonusItem[]>([]);
  const [loadingList, setLoadingList] = useState(true);

  // Give bonus form state
  const [bonusType, setBonusType] = useState('vip_bonus');
  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const inputStyles = {
    input: {
      color: 'var(--text-primary)',
      backgroundColor: 'var(--bg-input)',
    },
    '& .MuiInputBase-input::placeholder': {
      color: 'var(--text-primary) !important',
      opacity: '1 !important',
    },
    '& .MuiInputBase-input:disabled': {
      WebkitTextFillColor: 'var(--text-primary) !important',
      color: 'var(--text-primary) !important',
      opacity: '0.5 !important',
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: 'var(--border-color)' },
      '&:hover fieldset': { borderColor: 'var(--text-secondary)' },
      '&.Mui-focused fieldset': { borderColor: 'var(--accent-blue)' },
    },
    '& .MuiSelect-select': {
      color: 'var(--text-primary)',
      backgroundColor: 'var(--bg-input)',
    },
    '& .MuiSvgIcon-root': {
      color: 'var(--text-secondary)',
    },
  };

  const fetchUserBonuses = useCallback(async () => {
    try {
      setLoadingList(true);
      const response = await bonusService.getUserBonuses(userId, {
        page,
        limit,
        sortField: 'id',
        sortValue: 'DESC',
      });
      let bonusesData = [];
      let totalDocs = 0;
      if (Array.isArray(response)) {
        bonusesData = response;
        totalDocs = response.length;
      } else if (response?.data?.docs) {
        bonusesData = response.data.docs;
        totalDocs = response.data.totalDocs || bonusesData.length;
      } else if (response?.docs) {
        bonusesData = response.docs;
        totalDocs = response.totalDocs || bonusesData.length;
      } else if (Array.isArray(response?.data)) {
        bonusesData = response.data;
        totalDocs = response.data.length;
      }
      setBonuses(bonusesData);
      setTotal(totalDocs);
    } catch (error: any) {
      console.error('Failed to fetch user bonuses:', error);
      dispatch(
        showToast({
          text: error.response?.data?.message || error.message || 'Failed to fetch user bonuses',
          type: 'error',
        }),
      );
    } finally {
      setLoadingList(false);
    }
  }, [userId, page, limit, dispatch]);

  useEffect(() => {
    if (activeTab === 0) {
      fetchUserBonuses();
    }
  }, [activeTab, fetchUserBonuses]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleNumericChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      if (val === '') {
        setter('');
        return;
      }
      // Allow digits and single decimal dot
      if (/^[0-9]*\.?[0-9]*$/.test(val)) {
        setter(val);
      }
    };

  const handleGiveBonus = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount) return;

    setIsSubmitting(true);
    try {
      const payload = {
        bonusType,
        amount: String(amount),
        currencyCode: 'USD',
        reason: comment || undefined,
      };

      await bonusService.giveUserBonus(userId, payload);

      dispatch(showToast({ text: 'Bonus given successfully', type: 'success' }));
      setAmount('');
      setComment('');
      // Navigate to the list tab
      setActiveTab(0);
    } catch (error: any) {
      console.error('Failed to give bonus:', error);
      dispatch(
        showToast({
          text: error.response?.data?.message || error.message || 'Failed to give bonus',
          type: 'error',
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatBonusType = (type: string) => {
    if (!type) return '';
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    {
      field: 'type',
      headerName: 'Bonus Type',
      width: 180,
      valueGetter: (value: any) => formatBonusType(value),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 120,
      valueFormatter: (value: any) => (value !== null && value !== undefined ? `$${value}` : '-'),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      valueGetter: (value: any) => (value ? String(value).toUpperCase() : '-'),
    },
    {
      field: 'createdAt',
      headerName: 'Date Given',
      width: 180,
      renderCell: (params: any) => (params.row.createdAt ? formatDate(params.row.createdAt) : '-'),
    },
  ];

  return (
    <Box className={styles.tabContent} sx={{ p: 0 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          textColor="inherit"
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              color: 'var(--text-secondary)',
              '&.Mui-selected': {
                color: 'white',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'var(--accent-blue)',
            },
          }}
        >
          <Tab label="User bonuses" />
          <Tab label="Give bonus" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Box>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h6"
              sx={{ color: 'var(--text-primary)', mb: 3, fontWeight: 600, fontSize: '1.1rem' }}
            >
              User Bonuses History
            </Typography>
          </Box>
          {loadingList ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <Box sx={{ width: '100%' }}>
              <CustomPagination
                count={total || 0}
                page={page - 1}
                onPageChange={(_, newPage) => setPage(newPage + 1)}
                rowsPerPage={limit}
                onRowsPerPageChange={(e) => {
                  setLimit(parseInt(e.target.value, 10));
                  setPage(1);
                }}
                rowsPerPageOptions={[5, 10, 20, 50]}
              />
              <DataGrid
                rows={bonuses}
                rowCount={total}
                columns={columns}
                getRowId={(row) => row.id || row._id || Math.random()}
                sx={commonGridStyles}
                disableColumnMenu
                autoHeight
                hideFooterPagination
              />
              <CustomPagination
                count={total || 0}
                page={page - 1}
                onPageChange={(_, newPage) => setPage(newPage + 1)}
                rowsPerPage={limit}
                onRowsPerPageChange={(e) => {
                  setLimit(parseInt(e.target.value, 10));
                  setPage(1);
                }}
                rowsPerPageOptions={[5, 10, 20, 50]}
              />
            </Box>
          )}
        </Box>
      )}

      {activeTab === 1 && (
        <Box
          sx={{
            p: 3,
            backgroundColor: 'var(--bg-panel-secondary)',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            maxWidth: 500,
          }}
        >
          <Typography
            variant="h6"
            sx={{ color: 'var(--text-primary)', mb: 3, fontWeight: 600, fontSize: '1.1rem' }}
          >
            Give Bonus to User
          </Typography>
          <Box
            component="form"
            onSubmit={handleGiveBonus}
            sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
          >
            <FormControl fullWidth sx={inputStyles}>
              <InputLabel sx={{ color: 'var(--text-secondary)' }}>Bonus Type</InputLabel>
              <Select
                value={bonusType}
                label="Bonus Type"
                onChange={(e) => setBonusType(e.target.value as string)}
              >
                <MenuItem value="vip_bonus">Vip Bonus</MenuItem>
                <MenuItem value="lossback">Lossback</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Amount ($)"
              variant="outlined"
              value={amount}
              onChange={handleNumericChange(setAmount)}
              placeholder="0.00"
              required
              disabled={isSubmitting}
              sx={inputStyles}
              InputLabelProps={{ sx: { color: 'var(--text-secondary)' } }}
            />

            <TextField
              fullWidth
              label="Comment"
              variant="outlined"
              multiline
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Reason for giving the bonus"
              disabled={isSubmitting}
              sx={inputStyles}
              InputLabelProps={{ sx: { color: 'var(--text-secondary)' } }}
            />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 1 }}>
              <Button
                onClick={() => {
                  setAmount('');
                  setComment('');
                  setActiveTab(0);
                }}
                variant="outlined"
                disabled={isSubmitting}
                sx={{
                  color: 'var(--text-primary)',
                  borderColor: 'var(--border-color)',
                  '&:hover': { borderColor: 'var(--text-secondary)' },
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting || !amount}
                sx={{
                  backgroundColor: 'var(--accent-green)',
                  color: 'white',
                  '&:hover': { backgroundColor: '#1e682c' },
                  '&.Mui-disabled': {
                    backgroundColor: 'var(--text-secondary)',
                    color: 'rgba(255,255,255,0.5)',
                  },
                }}
              >
                {isSubmitting ? 'Processing...' : 'Give Bonus'}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default UserBonuses;
